import { useLogin } from '@/hooks/useAuth';
import { LoginCredentials } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { DataProtectionLink } from '../DataProtectionLink';
import { OtpVerificationForm } from './OtpVerificationForm';
import { TwoFactorVerificationForm } from './TwoFactorVerificationForm';

const loginSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email.invalid')
    .min(1, 'auth.validation.email.required'),
  password: z.string().min(1, 'auth.validation.password.required'),
});

export const LoginForm = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorTempToken, setTwoFactorTempToken] = useState<string | null>(
    null
  );
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  // [AUTO-INIT VAULT AU LOGIN 2026-07] Mot de passe conservé en mémoire le temps
  // de l'étape code (OTP/2FA), pour initialiser automatiquement un coffre en
  // attente sans rien redemander à l'utilisateur. Jamais persisté.
  const [loginPassword, setLoginPassword] = useState<string>('');

  const login = useLogin(
    (tempToken, password) => {
      setLoginPassword(password);
      setTwoFactorTempToken(tempToken);
    },
    (sessionId, password) => {
      setLoginPassword(password);
      setOtpSessionId(sessionId);
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginCredentials) => {
    try {
      await login.mutateAsync(data);
    } catch {
      // Error is handled by the useLogin hook with toast
    }
  };

  // If OTP is required, show OTP verification form
  if (otpSessionId) {
    return (
      <OtpVerificationForm
        sessionId={otpSessionId}
        loginPassword={loginPassword}
        onBack={() => {
          setLoginPassword('');
          setOtpSessionId(null);
        }}
      />
    );
  }

  // If 2FA is required, show verification form
  if (twoFactorTempToken) {
    return (
      <TwoFactorVerificationForm
        tempToken={twoFactorTempToken}
        loginPassword={loginPassword}
        onBack={() => {
          setLoginPassword('');
          setTwoFactorTempToken(null);
        }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-neutral-700"
        >
          {t('auth.login.email')}
        </label>
        <input
          {...register('email')}
          type="email"
          id="email"
          className="mt-1 block w-full rounded-xl border px-3 py-2 text-base shadow-sm transition-colors placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none"
          style={{
            borderColor: 'var(--marque-trait)',
            outlineColor: 'var(--marque-accent)',
          }}
          placeholder={t('auth.login.emailPlaceholder')}
        />
        {errors.email && (
          <p className="text-error-600 mt-1 text-sm">
            {t(errors.email.message as string)}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-neutral-700"
        >
          {t('auth.login.password')}
        </label>
        <div className="relative mt-1">
          <input
            {...register('password')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            className="block w-full rounded-xl border px-3 py-2 pr-11 text-base shadow-sm transition-colors placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none"
            style={{
              borderColor: 'var(--marque-trait)',
              outlineColor: 'var(--marque-accent)',
            }}
            placeholder={t('auth.login.passwordPlaceholder')}
          />
          <button
            type="button"
            onClick={() => setShowPassword(current => !current)}
            className="focus:ring-brand-100 absolute top-1/2 right-0 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 transition-colors hover:text-neutral-700 focus:ring-2 focus:outline-none"
            aria-label={
              showPassword
                ? t('auth.login.hidePassword', 'Masquer le mot de passe')
                : t('auth.login.showPassword', 'Afficher le mot de passe')
            }
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="text-error-600 mt-1 text-sm">
            {t(errors.password.message as string)}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm">
          <Link
            to="/forgot-password"
            className="inline-flex min-h-11 items-center font-medium transition-colors hover:opacity-80"
            style={{ color: 'var(--marque-accent)' }}
          >
            {t('auth.login.forgotPassword')}
          </Link>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          // [C2 — marque blanche] L'accent vient de la marque. La variable a
          // toujours une valeur (le thème maison la définit), donc l'écran
          // maison est inchangé.
          className="focus:ring-primary-500 flex min-h-11 w-full justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-[filter] hover:brightness-90 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
          style={{
            backgroundColor: 'var(--marque-accent)',
            color: 'var(--marque-sur-accent)',
          }}
        >
          {isSubmitting ? t('common.loading') : t('auth.login.submit')}
        </button>
      </div>

      <div className="text-center text-sm">
        <span className="text-neutral-600">{t('auth.login.noAccount')}</span>{' '}
        <Link
          to="/register"
          className="inline-flex min-h-11 items-center font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--marque-accent)' }}
        >
          {t('auth.login.register')}
        </Link>
      </div>

      <DataProtectionLink />
    </form>
  );
};
